import { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Package, Plus, Download } from 'lucide-react';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

interface VerificationPackage {
  id: string;
  licenseKey: string;
  systemSignature: string;
  packageData: string;
  status: string;
  createdAt: number;
  expiresAt?: number;
}

function VerificationPackages() {
  const [packages, setPackages] = useState<VerificationPackage[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [newPackage, setNewPackage] = useState({
    licenseId: '',
    systemSignatureId: '',
  });

  useEffect(() => {
    fetchPackages();
  }, []);

  const fetchPackages = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/verification`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (response.ok) {
        const data = await response.json();
        setPackages(data.data || []);
      }
    } catch (error) {
      console.error('Failed to fetch verification packages:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleCreatePackage = async () => {
    try {
      const token = localStorage.getItem('token');
      const apiUrl = import.meta.env.VITE_API_URL || '';

      const response = await fetch(`${apiUrl}/api/verification-packages`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(newPackage),
      });

      if (response.ok) {
        setIsCreateDialogOpen(false);
        setNewPackage({ licenseId: '', systemSignatureId: '' });
        fetchPackages();
      }
    } catch (error) {
      console.error('Failed to create verification package:', error);
    }
  };

  const handleDownloadPackage = (pkg: VerificationPackage) => {
    try {
      // Parse packageData if it's a string
      const packageData = typeof pkg.packageData === 'string'
        ? JSON.parse(pkg.packageData)
        : pkg.packageData;

      // Create blob from JSON data
      const blob = new Blob([JSON.stringify(packageData, null, 2)], { type: 'application/json' });

      // Create download link
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `verification-package-${pkg.id}.json`;

      // Trigger download
      document.body.appendChild(link);
      link.click();

      // Cleanup
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Failed to download verification package:', error);
    }
  };

  const getStatusBadgeColor = (status: string) => {
    const colors: Record<string, string> = {
      ACTIVE: 'bg-green-500',
      USED: 'bg-blue-500',
      EXPIRED: 'bg-gray-500',
    };
    return colors[status] || 'bg-gray-500';
  };

  if (isLoading) {
    return (
      <div className="container mx-auto py-6">
        <Card>
          <CardContent className="pt-6">
            <p className="text-center text-muted-foreground">Loading...</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="container mx-auto py-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl font-bold tracking-tight flex items-center gap-2">
            <Package className="h-8 w-8" />
            Verification Packages
          </h2>
          <p className="text-muted-foreground mt-2">
            Air-gap license verification package management
          </p>
        </div>
        <Button onClick={() => setIsCreateDialogOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          Create Package
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>All Verification Packages</CardTitle>
          <CardDescription>
            {packages.length} verification package{packages.length !== 1 ? 's' : ''} created
          </CardDescription>
        </CardHeader>
        <CardContent>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>License Key</TableHead>
                <TableHead>System Signature</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Created</TableHead>
                <TableHead>Expires</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {packages.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center text-muted-foreground">
                    No verification packages found
                  </TableCell>
                </TableRow>
              ) : (
                packages.map((pkg) => (
                  <TableRow key={pkg.id}>
                    <TableCell className="font-mono text-xs max-w-xs truncate">
                      {pkg.licenseKey}
                    </TableCell>
                    <TableCell className="font-mono text-xs max-w-xs truncate">
                      {pkg.systemSignature}
                    </TableCell>
                    <TableCell>
                      <Badge className={getStatusBadgeColor(pkg.status)}>
                        {pkg.status}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      {new Date(pkg.createdAt * 1000).toLocaleDateString()}
                    </TableCell>
                    <TableCell>
                      {pkg.expiresAt
                        ? new Date(pkg.expiresAt * 1000).toLocaleDateString()
                        : 'Never'}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="ghost" size="sm" onClick={() => handleDownloadPackage(pkg)}>
                        <Download className="h-4 w-4 mr-1" />
                        Download
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Create Verification Package Dialog */}
      <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Verification Package</DialogTitle>
            <DialogDescription>
              Generate an air-gap verification package for offline license validation
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="licenseId">License ID</Label>
              <Input
                id="licenseId"
                value={newPackage.licenseId}
                onChange={(e) => setNewPackage({ ...newPackage, licenseId: e.target.value })}
                placeholder="Enter license ID or key"
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="systemSignatureId">System Signature ID</Label>
              <Input
                id="systemSignatureId"
                value={newPackage.systemSignatureId}
                onChange={(e) => setNewPackage({ ...newPackage, systemSignatureId: e.target.value })}
                placeholder="Enter system signature ID"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsCreateDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleCreatePackage}
              disabled={!newPackage.licenseId || !newPackage.systemSignatureId}
            >
              Create Package
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

export default VerificationPackages;
